import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/client";
import type { TripSummary } from "./tripStore";

export type TripInvitationDecision = "accepted" | "declined";
export type TripInvitationStatus = "pending" | "accepted" | "declined";

export interface SentTripInvitation {
  invitationId: string;
  tripId: string;
  identifier: string;
  participantRole: string;
  status: TripInvitationStatus;
  targetUid?: string;
  targetEmail?: string;
  displayName?: string;
  existingUser: boolean;
  emailQueued: boolean;
}

export interface TripInvitationStatusItem {
  invitationId: string;
  identifier: string;
  participantRole: string;
  status: TripInvitationStatus;
  targetUid?: string;
  targetEmail?: string;
  displayName?: string;
  external?: boolean;
}

export async function sendTripInvitation(input: {
  trip: TripSummary;
  identifier: string;
  participantRole: string;
  notes?: string;
  note?: string;
  workspace?: Record<string, unknown>;
}): Promise<SentTripInvitation> {
  const callable = httpsCallable<
    { trip: TripSummary; identifier: string; participantRole: string; notes?: string; note?: string; workspace?: Record<string, unknown> },
    SentTripInvitation
  >(functions, "sendTripInvitation");
  return (await callable(input)).data;
}

export async function listSentTripInvitations(tripId: string): Promise<TripInvitationStatusItem[]> {
  const callable = httpsCallable<{ tripId: string }, { items: TripInvitationStatusItem[] }>(functions, "listTripInvitations");
  return (await callable({ tripId })).data.items;
}

export async function respondTripInvitation(invitationId: string, decision: TripInvitationDecision): Promise<{
  decision: TripInvitationDecision;
  trip?: TripSummary;
  alreadyResolved?: boolean;
}> {
  const callable = httpsCallable<
    { invitationId: string; decision: TripInvitationDecision },
    { decision: TripInvitationDecision; trip?: TripSummary; alreadyResolved?: boolean }
  >(functions, "respondTripInvitation");
  return (await callable({ invitationId, decision })).data;
}


export async function resendTripInvitationEmail(invitationId: string): Promise<void> {
  const callable = httpsCallable<{ invitationId: string }, { queued: boolean }>(functions, "resendTripInvitationEmail");
  await callable({ invitationId });
}


export async function removeTripParticipantAccess(input: { tripId: string; targetUid?: string; invitationId?: string }): Promise<void> {
  const callable = httpsCallable<typeof input, { removed: boolean }>(functions, "removeTripParticipant");
  await callable(input);
}

export async function updateTripParticipantRole(input: { tripId: string; targetUid?: string; invitationId?: string; participantRole: string }): Promise<void> {
  const callable = httpsCallable<typeof input, { updated: boolean }>(functions, "updateTripParticipantRole");
  await callable(input);
}

export async function previewTripInvitation(invitationId: string): Promise<{ trip: TripSummary; workspace: Record<string, unknown>; participantRole: string }> {
  const callable = httpsCallable<{ invitationId: string }, { trip: TripSummary; workspace: Record<string, unknown>; participantRole: string }>(functions, "previewTripInvitation");
  return (await callable({ invitationId })).data;
}

export interface TripParticipantOverviewItem {
  uid: string;
  displayName: string;
  email?: string;
  participantRole: string;
  isOwner: boolean;
  joinedAt?: string;
}

export interface TripDepartedParticipantItem extends TripParticipantOverviewItem {
  leftAt: string;
}

export async function getTripParticipantsOverview(tripId: string): Promise<{
  current: TripParticipantOverviewItem[];
  departed: TripDepartedParticipantItem[];
}> {
  const callable = httpsCallable<
    { tripId: string },
    { current: TripParticipantOverviewItem[]; departed: TripDepartedParticipantItem[] }
  >(functions, "getTripParticipantsOverview");
  return (await callable({ tripId })).data;
}

export async function leaveTrip(tripId: string): Promise<void> {
  const callable = httpsCallable<{ tripId: string }, { left: boolean }>(functions, "leaveTrip");
  await callable({ tripId });
}
